// UTM Tracking Utility
// Captures and persists acquisition context for attribution analysis.
//
// This captures on EVERY first visit, not just visits carrying UTM parameters.
// Organic search and direct traffic carry no utm_source, so a UTM-only capture
// leaves the vast majority of signups with empty attribution - which is exactly
// what happened before: 4,186 of 4,190 user_signed_up events had no first_touch.
// The landing page and referring domain are always available, so we store those.

import { isReferralCode } from './referralCode';
// #485: classify an AI-assistant referral so a query can filter on it.
// The source of truth is shared/aiReferrer.js (the server's copy). This is the
// generated client-side copy, kept in sync by scripts/sync-ai-referrer.js and
// pinned by a test, because the client cannot import from outside client/.
import { classifyAIVisit } from '@/shared/aiReferrer';

const UTM_STORAGE_KEY = 'delphina_utm_params';
const UTM_EXPIRY_DAYS = 90;

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  first_touch?: string;
  last_touch?: string;
  landing_page?: string;
  landing_referrer?: string;
  cta_ref?: string;
  // #485: the AI assistant a visit came from, or absent. Absent rather than
  // empty, so a query can test it either way.
  ai_referrer?: string;
  captured_at?: string;
}

function retrieveAndClearExpiredUTM(): UTMParams | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(UTM_STORAGE_KEY);
    if (!stored) return null;

    const data = JSON.parse(stored) as UTMParams & { captured_at: string };

    // Check if expired
    if (data.captured_at) {
      const capturedDate = new Date(data.captured_at);
      const now = new Date();
      const daysDiff = (now.getTime() - capturedDate.getTime()) / (1000 * 60 * 60 * 24);

      if (daysDiff > UTM_EXPIRY_DAYS) {
        localStorage.removeItem(UTM_STORAGE_KEY);
        return null;
      }
    }

    return data;
  } catch {
    return null;
  }
}

/**
 * The referring domain, but only when the visitor arrived from somewhere other
 * than this site. Internal navigation must not overwrite acquisition source -
 * otherwise every click through the site rewrites last_touch to 'delphina.money'.
 */
function externalReferrerDomain(): string | null {
  if (typeof window === 'undefined') return null;

  const referrer = document.referrer;
  if (!referrer) return null;

  try {
    const host = new URL(referrer).hostname;
    return host === window.location.hostname ? null : host;
  } catch {
    return null;
  }
}

function saveUTM(params: UTMParams): void {
  if (typeof window === 'undefined') return;

  try {
    const existing = retrieveAndClearExpiredUTM();

    // What brought them here this time. Null means internal navigation, which
    // tells us nothing new about acquisition, so we leave the stored values be.
    // A first-ever visit with no referrer is genuinely 'direct'.
    const incomingSource =
      params.utm_source || externalReferrerDomain() || (existing ? null : 'direct');

    // Only let defined values overwrite what we already stored: a later visit
    // without UTMs must not blank out the campaign that originally brought them.
    const incomingDefined = Object.fromEntries(
      Object.entries(params).filter(([, value]) => value !== undefined && value !== '')
    );

    const landingReferrer = existing?.landing_referrer || externalReferrerDomain() || '(direct)';

    // #485: the AI assistant a visit came from. The referrer wins over the
    // utm_source, because ChatGPT appends `utm_source=chatgpt.com` to the links
    // it generates while the referrer says where the click actually came from.
    //
    // First touch wins, like the other acquisition fields: a visitor who
    // arrived from ChatGPT and later browsed the site is still an AI referral.
    const aiReferrer =
      existing?.ai_referrer ||
      classifyAIVisit({
        referrer: externalReferrerDomain(),
        utmSource: params.utm_source,
      }) ||
      undefined;

    const utmData: UTMParams = {
      ...existing,
      ...incomingDefined,
      first_touch: existing?.first_touch || incomingSource || '',
      last_touch: incomingSource || existing?.last_touch || '',
      landing_page: existing?.landing_page || window.location.pathname,
      landing_referrer: landingReferrer,
      // Last CTA wins: this is the click that carried them into the signup flow.
      cta_ref: params.cta_ref || existing?.cta_ref || '',
      ...(aiReferrer ? { ai_referrer: aiReferrer } : {}),
      captured_at: existing?.captured_at || new Date().toISOString()
    };

    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmData));
  } catch (e) {
    console.error('Failed to save UTM params:', e);
  }
}

/**
 * #464: capture a `?ref=` from the current URL into the stored attribution.
 *
 * The module-load `captureUTMFromURL()` below only runs when `utmTracking.ts`
 * is first imported. That is enough for a page load, but a CTA that links to
 * `/login?ref=<slug>` is a CLIENT-SIDE route transition in Next.js, so by the
 * time the visitor arrives the module is already loaded and the URL is never
 * re-read. The ref was therefore dropped on every CTA click.
 *
 * Measured before the fix, walking the real journey (land on a marketing page,
 * then a client-side navigation to `/login?ref=monarch-bottom`):
 *
 *   cta_ref      = ""      <- the ref is lost
 *   landing_page = "/blog/top-5-monarch-alternatives-uk-2026"   <- correct
 *
 * The fix is to call this from the page that receives the ref. `/login` already
 * reads `?ref=` for its own context, so it is the natural place: one read of
 * the URL, one place that persists it.
 *
 * Only the ref is touched. The first-touch landing page must NOT be
 * overwritten with `/login` - that is the page the visitor came FROM, and
 * replacing it would destroy the first-touch attribution that #430 and #452
 * depend on.
 */
export function captureCTARefFromURL(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    const ref = new URLSearchParams(window.location.search).get('ref');
    if (!ref) return null;

    // Content CTAs tag themselves with ?ref=<slug>. Genuine referral codes
    // share the parameter but are handled by the referral flow, so they are not
    // acquisition attribution.
    if (isReferralCode(ref)) return null;

    const existing = retrieveAndClearExpiredUTM();
    if (!existing) {
      // Nothing stored yet, so there is no first touch to protect. Write the
      // ref on its own and let the next captureUTMFromURL fill in the rest.
      localStorage.setItem(
        UTM_STORAGE_KEY,
        JSON.stringify({ cta_ref: ref, captured_at: new Date().toISOString() })
      );
      return ref;
    }

    // Last CTA wins: this is the click that carried them into the signup flow.
    if (existing.cta_ref === ref) return ref;

    localStorage.setItem(
      UTM_STORAGE_KEY,
      JSON.stringify({ ...existing, cta_ref: ref })
    );
    return ref;
  } catch (e) {
    console.error('Failed to capture CTA ref:', e);
    return null;
  }
}

/**
 * Captures acquisition context from the current URL.
 *
 * Always records first-touch landing page and referrer; records UTM values too
 * when the URL carries them.
 */
export function captureUTMFromURL(): UTMParams | null {
  if (typeof window === 'undefined') return null;

  const urlParams = new URLSearchParams(window.location.search);

  const utmSource = urlParams.get('utm_source');
  const utmMedium = urlParams.get('utm_medium');
  const utmCampaign = urlParams.get('utm_campaign');
  const utmTerm = urlParams.get('utm_term');
  const utmContent = urlParams.get('utm_content');

  // Content CTAs tag themselves with ?ref=<slug> (e.g. monarch-bottom). Genuine
  // referral codes share the parameter but are handled by the referral flow, so
  // they are not acquisition attribution.
  const ref = urlParams.get('ref');
  const ctaRef = ref && !isReferralCode(ref) ? ref : undefined;

  const params: UTMParams = {
    utm_source: utmSource || undefined,
    utm_medium: utmMedium || undefined,
    utm_campaign: utmCampaign || undefined,
    utm_term: utmTerm || undefined,
    utm_content: utmContent || undefined,
    cta_ref: ctaRef
  };

  saveUTM(params);

  return utmSource || utmMedium || utmCampaign || utmTerm || utmContent ? params : null;
}

export function retrieveAndClearExpiredUTMParams(): UTMParams | null {
  return retrieveAndClearExpiredUTM();
}

export function getUTMForAnalytics(): Record<string, string> {
  const utm = retrieveAndClearExpiredUTM();
  if (!utm) return {};

  return {
    utm_source: utm.utm_source || '',
    utm_medium: utm.utm_medium || '',
    utm_campaign: utm.utm_campaign || '',
    utm_term: utm.utm_term || '',
    utm_content: utm.utm_content || '',
    first_touch: utm.first_touch || '',
    last_touch: utm.last_touch || '',
    landing_page: utm.landing_page || '',
    landing_referrer: utm.landing_referrer || '',
    cta_ref: utm.cta_ref || '',
    // #485: only present when the visit came from an AI assistant, so an
    // absent property means "not an AI referral" rather than "unknown".
    ...(utm.ai_referrer ? { ai_referrer: utm.ai_referrer } : {})
  };
}

export function clearUTMParams(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(UTM_STORAGE_KEY);
}

// Initialize on page load
if (typeof window !== 'undefined') {
  // Capture UTM on page load
  captureUTMFromURL();
}
